import { createTheme, ThemeProvider } from "@mui/material/styles";
import { configureStore } from "@reduxjs/toolkit";
import ReactDOM from "react-dom/client";
import { Provider as ReduxProvider, useDispatch } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { combineReducers } from "redux";
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import storage from "redux-persist/lib/storage";
import ReduxThunk from "redux-thunk";
import AppRoutes from "./AppRoutes";
import EnvironmentBanner from "./components/UI/environmentBanner/EnvironmentBanner";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { AgendaReducer } from "./store/reducers/agenda";
import { AllergiesReducer } from "./store/reducers/allergies";
import { AppointmentReducer } from "./store/reducers/appointment";
import { CareLineReducer } from "./store/reducers/careLine";
import { CollaboratorInitial } from "./store/reducers/collaboratorInitial";
import { CompanyReducer } from "./store/reducers/company";
import { ExamesReducer } from "./store/reducers/exams";
import { LoginReducer } from "./store/reducers/login";
import { OccupationalHealthReducer } from "./store/reducers/occupationalHealth";
import { PersonReducer } from "./store/reducers/person";
import { QuestionnaireReducer } from "./store/reducers/questionnaire";
import { relationshipGheReducer } from "./store/reducers/relationshipGhe";
import { SchedulingReducer } from "./store/reducers/scheduling";
import { UnitReducer } from "./store/reducers/unit";
import { UserRoleReducer } from "./store/reducers/userRole";
import { register } from "./serviceWorkerRegistration";
import { changeAgendamentoReducer } from "./store/reducers/attendanceRecord";
import { MedicalRecordReducer } from "./store/reducers/medicalRecord";
import { VitalSignReducer } from "./store/reducers/vitalSign";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const rootReduxReducer = combineReducers({
  login: LoginReducer,
  person: PersonReducer,
  company: CompanyReducer,
  unit: UnitReducer,
  userRole: UserRoleReducer,
  careLine: CareLineReducer,
  occupationalHealth: OccupationalHealthReducer,
  questionnaire: QuestionnaireReducer,
  agenda: AgendaReducer,
  appointment: AppointmentReducer,
  scheduling: SchedulingReducer,
  collaborationInitial: CollaboratorInitial,
  exams: ExamesReducer,
  relationShip: relationshipGheReducer,
  allergies: AllergiesReducer,
  attendance: changeAgendamentoReducer,
  medicalRecord: MedicalRecordReducer,
  vitalSign: VitalSignReducer,
});

const persistConfig = {
  key: "root",
  storage,
};

const persistedReducer = persistReducer(persistConfig, rootReduxReducer);

const reduxStore = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(ReduxThunk),
});

const persistor = persistStore(reduxStore);

const theme = createTheme({
  typography: {
    fontFamily: "Inter",
  },
});

export type RootState = ReturnType<typeof reduxStore.getState>;
export type AppDispatch = typeof reduxStore.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();

// PWA
register();

root.render(
  <ReduxProvider store={reduxStore}>
    {/* <React.StrictMode> */}
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <PersistGate loading={null} persistor={persistor}>
          <EnvironmentBanner />
          <AppRoutes />
        </PersistGate>
      </ThemeProvider>
    </BrowserRouter>
    {/* </React.StrictMode> */}
  </ReduxProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
